import { computed, inject, Injectable, signal } from '@angular/core';
import { getBrowserCultureLang, TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import { availableLocales, defaultLocale, LocaleKey } from './i18n-locales';

/** New (shared) storage key. */
const STORAGE_KEY = 'wheere.lang.v1';

/** Legacy per-app key, read once on bootstrap and migrated to the shared key. */
const LEGACY_STORAGE_KEYS = ['wheere.integrator.lang.v1'];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  static readonly STORAGE_KEY = STORAGE_KEY;

  /** Whatever the rest of the app should bind to. Initialised from
   * localStorage / browser detection / defaultLocale (in that order). */
  readonly active = signal<string>(this.resolveInitial());

  /** Convenience label for the navbar switcher — uppercase 2-letter code
   * derived from the locale ({@code en-US → EN}, {@code fr-FR → FR}). */
  readonly shortLabel = computed(() => this.active().slice(0, 2).toUpperCase());

  #transloco = inject(TranslocoService);

  constructor() {
    this.#syncSideEffects(this.active());
  }

  /** Set a specific locale. Idempotent — same lang in = no work done. */
  set(lang: string): void {
    if (this.active() === lang) return;
    if (!availableLocales.includes(lang)) return;
    this.active.set(lang);
    this.#syncSideEffects(lang);
    this.#saveToStorage(lang);
  }

  /** Toggle between {@code en-US} and {@code fr-FR}. We only ship two
   * locales today, so a binary cycle is sufficient. If we add more, this
   * becomes a rotation through {@link availableLocales}. */
  cycle(): void {
    const next = this.active() === LocaleKey.FR_FR ? LocaleKey.EN_US : LocaleKey.FR_FR;
    this.set(next);
  }

  // ── Internals ───────────────────────────────────────────────────────────

  private resolveInitial(): string {
    // 1. localStorage takes priority — user already made an explicit choice.
    const stored = this.#loadFromStorage();
    if (stored && availableLocales.includes(stored)) return stored;
    // 2. Browser hint via Transloco's helper. Anything {@code fr-*} → fr-FR.
    try {
      const culture = getBrowserCultureLang();
      if (culture && culture.toLowerCase().startsWith('fr')) return LocaleKey.FR_FR;
    } catch {
      // SSR / older browsers without navigator — fall through.
    }
    // 3. Static default.
    return defaultLocale;
  }

  #syncSideEffects(lang: string): void {
    this.#transloco.setActiveLang(lang);
    this.#transloco.load(lang).subscribe({
      // Errors are surfaced by the loader's own catchError (which logs and
      // returns an empty translation). Nothing more to do here.
      error: () => undefined,
    });
    try {
      dayjs.locale(lang.split('-')[0]);
    } catch {
      // ignore — dayjs/locale/fr + en are imported at the top of this file.
    }
  }

  #loadFromStorage(): string | null {
    try {
      const fresh = localStorage.getItem(STORAGE_KEY);
      if (fresh) return fresh;
      for (const legacy of LEGACY_STORAGE_KEYS) {
        const stored = localStorage.getItem(legacy);
        if (stored) {
          try {
            localStorage.setItem(STORAGE_KEY, stored);
            localStorage.removeItem(legacy);
          } catch {
            // best-effort migration
          }
          return stored;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  #saveToStorage(lang: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore — non-fatal, the signal value still holds for the session.
    }
  }
}
